import { useEffect, useMemo, useRef, useState } from "react";
import api, { API, apiErrorMessage } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Minus, ChevronRight, Upload, Download, Filter, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ALL_TIERS = ["Standard", "Silver", "Gold", "Platinum", "VIP"];

export default function AdminPatients() {
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    tier: "all",
    min_points: "",
    max_points: "",
    joined_from: "",
    joined_to: "",
    active_30d: false,
  });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pointsForm, setPointsForm] = useState({ points: 100, reason: "", service_type: "" });

  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "", email: "", phone: "", date_of_birth: "",
    starting_points: 0, tier: "Standard", send_invite_email: true,
  });
  const [addResult, setAddResult] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/patients${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setPatients(data);
  };
  useEffect(() => { load(); }, [q]);

  const filtered = useMemo(() => {
    const cutoffStr = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const min = filters.min_points === "" ? -Infinity : Number(filters.min_points);
    const max = filters.max_points === "" ? Infinity : Number(filters.max_points);
    return patients.filter((p) => {
      if (filters.tier !== "all" && p.tier !== filters.tier) return false;
      if (p.points < min || p.points > max) return false;
      if (filters.joined_from && new Date(p.created_at) < new Date(filters.joined_from)) return false;
      if (filters.joined_to && new Date(p.created_at) > new Date(filters.joined_to + "T23:59:59")) return false;
      if (filters.active_30d) {
        // Approximation client-side: not all clients store last_activity; the API returns created_at only.
        // We treat patients created in last 30 days OR with > 0 points changes recently as active. For now,
        // we use created_at >= cutoff OR points > 0 as a soft heuristic.
        if (new Date(p.created_at) < new Date(cutoffStr) && p.points === 0) return false;
      }
      return true;
    });
  }, [patients, filters]);

  const openDetail = async (p) => {
    setSelected(p);
    setDetail(null);
    const { data } = await api.get(`/admin/patients/${p.id}`);
    setDetail(data);
  };

  const award = async () => {
    try {
      await api.post("/admin/award-points", { user_id: selected.id, ...pointsForm, points: Number(pointsForm.points) });
      toast.success("Points updated");
      await openDetail(selected);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/admin/patient-import/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "medrewards_patients_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const runImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/admin/patients/bulk", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Imported: ${data.created} created · ${data.updated} updated · ${data.skipped} skipped`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Import failed"));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetFilters = () => setFilters({ tier: "all", min_points: "", max_points: "", joined_from: "", joined_to: "", active_30d: false });

  const resetAddForm = () => setAddForm({
    name: "", email: "", phone: "", date_of_birth: "",
    starting_points: 0, tier: "Standard", send_invite_email: true,
  });

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/admin/patients", {
        ...addForm,
        starting_points: Number(addForm.starting_points) || 0,
      });
      setAddResult(data);
      toast.success(`${data.patient.name} added${addForm.send_invite_email ? " — invite email sent" : ""}`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not create patient"));
    } finally {
      setAdding(false);
    }
  };

  return (
    <AdminShell title="Patients">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-mr-secondary" />
          <Input data-testid="patients-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 w-80 rounded-full bg-white border-mr h-10" />
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="patients-download-template-btn" variant="outline" className="rounded-full" onClick={downloadTemplate}>
            <Download className="h-4 w-4 mr-2" /> CSV template
          </Button>
          <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) { setAddResult(null); resetAddForm(); } }}>
            <DialogTrigger asChild>
              <Button data-testid="patients-add-open-btn" variant="outline" className="rounded-full border-mr">
                <UserPlus className="h-4 w-4 mr-2" /> Add patient
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Add a patient</DialogTitle></DialogHeader>
              {!addResult ? (
                <form onSubmit={submitAdd} className="space-y-4">
                  <p className="text-sm text-mr-secondary">We'll auto-generate a temporary password and email an invite + their digital membership card.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label>Full name</Label>
                      <Input data-testid="add-patient-name" required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Hassan Yahia" className="mt-2" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Email</Label>
                      <Input data-testid="add-patient-email" type="email" required value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="patient@example.com" className="mt-2" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input data-testid="add-patient-phone" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} placeholder="+971..." className="mt-2" />
                    </div>
                    <div>
                      <Label>Date of birth</Label>
                      <Input data-testid="add-patient-dob" type="date" value={addForm.date_of_birth} onChange={(e) => setAddForm({ ...addForm, date_of_birth: e.target.value })} className="mt-2" />
                    </div>
                    <div>
                      <Label>Initial tier</Label>
                      <Select value={addForm.tier} onValueChange={(v) => setAddForm({ ...addForm, tier: v })}>
                        <SelectTrigger data-testid="add-patient-tier" className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ALL_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Starting points</Label>
                      <Input data-testid="add-patient-points" type="number" min={0} value={addForm.starting_points} onChange={(e) => setAddForm({ ...addForm, starting_points: e.target.value })} className="mt-2" />
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-3 mt-1">
                      <Switch data-testid="add-patient-invite" checked={addForm.send_invite_email} onCheckedChange={(v) => setAddForm({ ...addForm, send_invite_email: v })} />
                      <Label className="text-sm">Email invite with temporary password & membership card</Label>
                    </div>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button data-testid="add-patient-submit-btn" type="submit" disabled={adding} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                      {adding ? "Adding…" : "Add patient"}
                    </Button>
                  </DialogFooter>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl bg-mr-bg p-4 text-sm">
                    <p className="text-mr-primary"><b>{addResult.patient.name}</b> was added to the programme.</p>
                    <p className="text-xs text-mr-secondary mt-2">Member ID · <span className="font-mono">{addResult.patient.id.slice(-8).toUpperCase()}</span></p>
                  </div>
                  <div className="rounded-xl border border-mr p-4 text-sm">
                    <p className="label-eyebrow text-mr-secondary">Temporary password</p>
                    <p className="font-mono text-mr-primary mt-1 break-all" data-testid="add-patient-temp-pass">{addResult.temp_password}</p>
                    <p className="text-xs text-mr-secondary mt-2">{addForm.send_invite_email ? "We emailed it along with the membership card." : "Share this with the patient — they'll need it to sign in."}</p>
                  </div>
                  <DialogFooter className="gap-2">
                    <Button type="button" variant="ghost" onClick={() => { setAddResult(null); resetAddForm(); }}>Add another</Button>
                    <Button onClick={() => { setAddOpen(false); }} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">Done</Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="patients-import-open-btn" className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">
                <Upload className="h-4 w-4 mr-2" /> Bulk import
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Bulk import patients</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-mr-secondary">Upload a CSV file with patients. Existing emails will be <b>updated</b>; new ones will be created with a welcome bonus.</p>
                <div className="rounded-xl bg-mr-bg p-4 text-xs">
                  <p className="font-medium text-mr-primary mb-1">Required columns:</p>
                  <code className="text-mr-secondary">name, email, password</code>
                  <p className="font-medium text-mr-primary mt-3 mb-1">Optional columns:</p>
                  <code className="text-mr-secondary">phone, date_of_birth, starting_points, referral_code</code>
                </div>
                <div>
                  <Button data-testid="patients-import-template-btn" variant="outline" className="w-full rounded-full" onClick={downloadTemplate}>
                    <Download className="h-4 w-4 mr-2" /> Download CSV template
                  </Button>
                </div>
                <div>
                  <input data-testid="patients-import-file" ref={fileRef} type="file" accept=".csv" onChange={runImport} className="hidden" />
                  <Button data-testid="patients-import-pick-btn" disabled={importing} className="w-full rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]" onClick={() => fileRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-2" /> {importing ? "Importing…" : "Choose CSV file"}
                  </Button>
                </div>
                {importResult && (
                  <div className="rounded-xl border border-mr p-4 text-sm">
                    <p className="text-mr-primary"><b>{importResult.created}</b> created · <b>{importResult.updated}</b> updated · <b>{importResult.skipped}</b> skipped</p>
                    {importResult.errors?.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-mr-accent cursor-pointer">View {importResult.errors.length} error(s)</summary>
                        <ul className="mt-2 text-xs text-mr-secondary space-y-1 max-h-32 overflow-auto">
                          {importResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter><Button variant="ghost" onClick={() => setImportOpen(false)}>Close</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-mr rounded-2xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3 text-mr-primary">
          <Filter className="h-4 w-4" />
          <p className="font-display font-semibold">Filters</p>
          <button data-testid="filters-reset-btn" onClick={resetFilters} className="ml-auto text-xs text-mr-accent">Reset</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Tier</Label>
            <Select value={filters.tier} onValueChange={(v) => setFilters({ ...filters, tier: v })}>
              <SelectTrigger data-testid="filter-tier" className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tiers</SelectItem>
                {ALL_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Min points</Label>
            <Input data-testid="filter-min" type="number" value={filters.min_points} onChange={(e) => setFilters({ ...filters, min_points: e.target.value })} className="mt-1 h-9" placeholder="0" />
          </div>
          <div>
            <Label className="text-xs">Max points</Label>
            <Input data-testid="filter-max" type="number" value={filters.max_points} onChange={(e) => setFilters({ ...filters, max_points: e.target.value })} className="mt-1 h-9" placeholder="∞" />
          </div>
          <div>
            <Label className="text-xs">Joined from</Label>
            <Input data-testid="filter-from" type="date" value={filters.joined_from} onChange={(e) => setFilters({ ...filters, joined_from: e.target.value })} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">Joined to</Label>
            <Input data-testid="filter-to" type="date" value={filters.joined_to} onChange={(e) => setFilters({ ...filters, joined_to: e.target.value })} className="mt-1 h-9" />
          </div>
          <div className="flex items-end gap-2">
            <Switch data-testid="filter-active30" checked={filters.active_30d} onCheckedChange={(v) => setFilters({ ...filters, active_30d: v })} />
            <Label className="text-xs">Active 30d</Label>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className="label-eyebrow text-mr-secondary">{filtered.length} of {patients.length} patients</span>
      </div>

      <div className="bg-white border border-mr rounded-2xl divide-y divide-mr-border">
        {filtered.map((p) => (
          <button key={p.id} data-testid={`patient-row-${p.id}`} onClick={() => openDetail(p)} className="w-full p-4 flex items-center justify-between hover:bg-[#F8FAFC] text-left">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#E2E8F0] text-mr-primary flex items-center justify-center font-display font-semibold">{p.name[0]}</div>
              <div>
                <p className="text-sm font-medium text-mr-primary">{p.name}</p>
                <p className="text-xs text-mr-secondary">{p.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Badge variant="outline" className="rounded-full border-mr">{p.tier}</Badge>
              <span className="font-display font-semibold text-mr-primary">{p.points.toLocaleString()}<span className="text-xs font-normal text-mr-secondary"> pts</span></span>
              <ChevronRight className="h-4 w-4 text-mr-secondary" />
            </div>
          </button>
        ))}
        {filtered.length === 0 && <p className="p-10 text-center text-mr-secondary">No patients match these filters.</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{selected?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#F8FAFC] rounded-xl p-4"><p className="label-eyebrow text-mr-secondary">Points</p><p className="font-display text-2xl font-bold text-mr-primary mt-1">{detail.patient.points.toLocaleString()}</p></div>
                <div className="bg-[#F8FAFC] rounded-xl p-4"><p className="label-eyebrow text-mr-secondary">Tier</p><p className="font-display text-2xl font-bold text-mr-primary mt-1">{detail.patient.tier}</p></div>
                <div className="bg-[#F8FAFC] rounded-xl p-4"><p className="label-eyebrow text-mr-secondary">Joined</p><p className="text-sm text-mr-primary mt-2">{new Date(detail.patient.created_at).toLocaleDateString()}</p></div>
              </div>

              <div className="border border-mr rounded-xl p-4">
                <h3 className="font-display font-semibold text-mr-primary mb-3">Award / deduct points</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Points (negative to deduct)</Label><Input data-testid="award-points-input" type="number" value={pointsForm.points} onChange={(e) => setPointsForm({ ...pointsForm, points: e.target.value })} /></div>
                  <div><Label>Service type (optional)</Label><Input data-testid="award-service-input" placeholder="annual_screening" value={pointsForm.service_type} onChange={(e) => setPointsForm({ ...pointsForm, service_type: e.target.value })} /></div>
                  <div className="sm:col-span-2"><Label>Reason</Label><Input data-testid="award-reason-input" value={pointsForm.reason} onChange={(e) => setPointsForm({ ...pointsForm, reason: e.target.value })} placeholder="Annual health screening" /></div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button data-testid="award-deduct-btn" variant="outline" className="rounded-full" onClick={() => setPointsForm({ ...pointsForm, points: -Math.abs(Number(pointsForm.points || 0)) })}><Minus className="h-4 w-4 mr-1" /> Make negative</Button>
                  <Button data-testid="award-submit-btn" onClick={award} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Plus className="h-4 w-4 mr-1" /> Apply</Button>
                </div>
              </div>

              <div>
                <h3 className="font-display font-semibold text-mr-primary mb-2">Recent transactions</h3>
                <div className="max-h-48 overflow-y-auto divide-y divide-mr-border border border-mr rounded-xl">
                  {detail.transactions.map((tx) => (
                    <div key={tx.id} className="p-3 flex items-center justify-between text-sm">
                      <div><p className="text-mr-primary">{tx.reason}</p><p className="text-xs text-mr-secondary">{new Date(tx.created_at).toLocaleString()}</p></div>
                      <span className={`font-display font-semibold ${tx.points > 0 ? "text-mr-success" : "text-mr-accent"}`}>{tx.points > 0 ? "+" : ""}{tx.points}</span>
                    </div>
                  ))}
                  {detail.transactions.length === 0 && <p className="p-4 text-sm text-mr-secondary text-center">No transactions.</p>}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="ghost" onClick={() => setSelected(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
